import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { Tooltip } from "bootstrap";
import { Modal } from "../utils/modal"

export default class extends Controller {
  static targets = [
    "urlTemplate", "quickReplyTemplate", "phoneTemplate",
    "template", "json", "text", "url", "quickReplyGroup", "websiteGroup",
    "buttonContainer", "quickGroupContainer", "websiteGroupContainer", "buttonType", "previewTrigger"
  ];

  static values = {
    minbuttons: Number,
    maxbuttons: Number,
    allowedButtons: String,
  }

  static allowedButtons = [];

  connect() {
    if (this.allowedButtonsValue) {
      this.allowedButtons = JSON.parse(this.allowedButtonsValue);
    }
    this.initializeSortableGroups();
    this.initializeSortable(this.quickReplyGroupTarget, 'quick_reply');
    this.initializeSortable(this.websiteGroupTarget, 'website');
    this.updateJSON(); // Initialize JSON when the page loads with existing fields
    this.toggleGroupVisibility();
  }

  initializeSortable(element, group) {
    Sortable.create(element, {
      group: group,
      handle: '.handle',
      animation: 150,
      onEnd: () => {
        this.updateJSON();
        this.triggerPreviewUpdate();
      }
    });
  }

  initializeSortableGroups() {
    Sortable.create(this.buttonContainerTarget, {
      handle: '.group-handle',
      animation: 150,
      onEnd: () => {
        this.updateJSON();
        this.triggerPreviewUpdate();
      }
    });
  }

  addButton() {
    const type = this.buttonTypeTarget.value.toLowerCase();
    const totalButtons = this.quickReplyGroupTarget.querySelectorAll('[data-new-button]').length +
                         this.websiteGroupTarget.querySelectorAll('[data-new-button]').length;

    if (!type) return;

    if (totalButtons >= this.maxbuttonsValue) {
      Modal.danger({
        title: window.I18n.modal.danger.title,
        message: window.I18n.notifications.max_buttons_reached,
        confirmText: window.I18n.general.okay
      });
      return;
    }

    if (this.isMaxReachedForType(type)) {
      Modal.danger({
        title: window.I18n.modal.danger.title,
        message: window.I18n.notifications.type_max_buttons_reached,
        confirmText: window.I18n.general.okay
      });
      return;
    }

    let content;
    switch (type) {
      case ("quick_reply"):
        content = this.quickReplyTemplateTarget.innerHTML;
        break;
      case ("url"):
        content = this.urlTemplateTarget.innerHTML;
        break;
      case ("phone_number"):
        content = this.phoneTemplateTarget.innerHTML;
        break;
    }

    if (!content) return;

    const container = type === "quick_reply" ? this.quickReplyGroupTarget : this.websiteGroupTarget;
    container.insertAdjacentHTML('beforeend', content);

    // Making all fields required within the template.
    for (const input of container.getElementsByTagName("input")) {
      input.setAttribute("required", true);
    }
    // Enabling tooltips as they seem to not work on elements added dynamically?!
    for (const tooltipElement of container.querySelectorAll("[data-bs-toggle=tooltip]")) {
      const tooltip = new Tooltip(tooltipElement);

      tooltip.enable();
    }

    this.updateJSON();
    this.triggerPreviewUpdate();
    this.toggleGroupVisibility();
  }

  removeButton(event) {
    const totalButtons = this.quickReplyGroupTarget.querySelectorAll('[data-new-button]').length +
                         this.websiteGroupTarget.querySelectorAll('[data-new-button]').length;

    if (totalButtons <= this.minbuttonsValue) {
      Modal.danger({
        title: window.I18n.modal.danger.title,
        message: window.I18n.notifications.min_buttons_reached,
        confirmText: window.I18n.general.okay
      });
      return;
    }

    event.target.closest('[data-new-button]').remove();
    this.updateJSON();
    this.triggerPreviewUpdate();
    this.toggleGroupVisibility();
  }

  toggleGroupVisibility() {
    const quickGroupContainer = this.quickGroupContainerTarget;
    const websiteGroupContainer = this.websiteGroupContainerTarget;

    quickGroupContainer.style.display = this.quickReplyGroupTarget.querySelectorAll('[data-new-button]').length > 0 ? 'block' : 'none';
    websiteGroupContainer.style.display = this.websiteGroupTarget.querySelectorAll('[data-new-button]').length > 0 ? 'block' : 'none';
  }

  updateJSON() {
    const buttons = this.collectButtonData();
    this.jsonTarget.value = JSON.stringify(buttons);
  }

  isMaxReachedForType(type) {
    const typeUpperCase = type.toUpperCase();
    const querySelector = '[data-button-type=' + typeUpperCase + ']';
    const typeTotalButtons = this.quickReplyGroupTarget.querySelectorAll(querySelector).length +
                             this.websiteGroupTarget.querySelectorAll(querySelector).length;
    const buttonSettings = this.allowedButtons ? this.allowedButtons.find((element) => element.type === typeUpperCase) : null;

    return buttonSettings && typeTotalButtons >= buttonSettings.max_count;
  }

  collectButtonData() {
    const buttons = [];
    const groups = this.buttonContainerTarget.children;

    Array.from(groups).forEach(group => {
      if (group.dataset.buttonsTarget === "websiteGroupContainer") {
        buttons.push(...this.collectButtonsFromGroup(this.websiteGroupTarget.querySelectorAll('[data-button-type="URL"]'), "URL"));
        buttons.push(...this.collectButtonsFromGroup(this.websiteGroupTarget.querySelectorAll('[data-button-type="PHONE_NUMBER"]'), "PHONE_NUMBER"));
      } else if (group.dataset.buttonsTarget === "quickGroupContainer") {
        buttons.push(...this.collectButtonsFromGroup(this.quickReplyGroupTarget.querySelectorAll('[data-button-type="QUICK_REPLY"]'), "QUICK_REPLY"));
      }
    });

    return buttons;
  }

  collectButtonsFromGroup(group, type) {
    return Array.from(group).map(buttonField => {
      const text = buttonField.querySelector('[data-buttons-target="text"]').value;
      const urlElement = buttonField.querySelector('input[data-buttons-target="url"]');
      const url = urlElement ? urlElement.value : null;

      if (type === "URL") {
        return {type, text, url};
      } else if (type === "PHONE_NUMBER") {
        return {type, text, phone_number: url};
      }

      return {type, text};
    });
  }

  triggerPreviewUpdate() {
    setTimeout(() => {
      const previewTrigger = this.previewTriggerTarget;
      previewTrigger.dispatchEvent(new Event('input'));
    })
  }
}
